import { useId, useState, type FormEvent } from 'react';
import { DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { Spinner } from '../../../ui/Spinner';
import { btnPrimary, btnQuiet, inputCls } from '../../../ui/styles';
import { FormError } from '../../shared/FormError';

/**
 * AVG-rechten in Gegevens: alles downloaden (art. 15/20) en het account verwijderen (art. 17).
 * Verwijderen in twee stappen, met het e-mailadres ter bevestiging; de server vraagt daarnaast
 * een recente login.
 */
export function DataRights() {
  const {
    account,
    downloadData,
    deleteAccount,
    signOut
  } = useSignedInAccount();
  const [downloading, setDownloading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [email, setEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 423 `reauth_required`: dezelfde uitweg als bij het wachtwoord, opnieuw inloggen. */
  const [reauth, setReauth] = useState(false);
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    const res = await downloadData();
    setDownloading(false);
    if (res.ok) toast.success('Je gegevens staan in je downloads');else toast.error('Downloaden lukte niet', {
      description: res.error
    });
  };
  const remove = async (e: FormEvent) => {
    e.preventDefault();
    if (deleting) return;
    if (email.trim().toLowerCase() !== account.customer.email.toLowerCase()) {
      setError('Typ het e-mailadres van je account, precies zoals hierboven.');
      return;
    }
    setDeleting(true);
    setError(null);
    const res = await deleteAccount(email);
    setDeleting(false);
    if (res.ok) toast.success('Je account is verwijderd', {
      description: 'Bedankt voor het vertrouwen. Je bent altijd welkom terug.'
    });else {
      setReauth(res.code === 'reauth_required');
      setError(res.error);
    }
  };
  return <section id="privacy" aria-labelledby="data-rights-title" className="mt-10 scroll-mt-24" data-id="element-6548" data-mp="39gh3hgwctzkc">
      <h2 id="data-rights-title" className="font-display text-[24px] font-light text-ink" data-id="element-6549" data-mp="3ei6g30z86laj">
        Privacy en je gegevens
      </h2>
      <div className="mt-4 divide-y divide-line border-y border-line" data-id="element-6550" data-mp="35g708bzy5n96">
        <div className="flex flex-wrap items-center justify-between gap-3 py-4" data-id="element-6551" data-mp="35iazuhqtc03n">
          <div className="min-w-0" data-id="element-6552" data-mp="3t6b2og1efgyl">
            <p className="text-[15px] text-ink" data-id="element-6553" data-mp="11z1nwx0bg96">Download alles wat we van je bewaren</p>
            <p className="mt-0.5 text-[13px] text-muted" data-id="element-6554" data-mp="13tc2hanjpz0">Gegevens, bestellingen, afspraken, huidplan en dagboek, in één bestand.</p>
          </div>
          <button type="button" onClick={() => void download()} disabled={downloading} className={btnQuiet} data-id="element-6555" data-mp="3oe9k6cdn0u04">
            {downloading ? <Spinner data-id="element-6556" data-mp="fxohfdblmx5g" /> : <DownloadIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6557" data-mp="fxy4as1cko6x" />}
            {downloading ? 'Even geduld…' : 'Download'}
          </button>
        </div>

        <div className="py-4" data-id="element-6558" data-mp="u3gnadbv71hz">
          {confirming ? <form onSubmit={e => void remove(e)} noValidate data-id="element-6559" data-mp="q3fwdt7kwxu1">
              <p className="text-[15px] text-ink" data-id="element-6560" data-mp="q2xvgj9z1vfs">Account verwijderen</p>
              <p className="mt-1 max-w-prose text-[14px] leading-relaxed text-muted" data-id="element-6561" data-mp="13t9nki6k3u6x">
                Je gegevens, huidplan, afspraken en foto’s worden verwijderd. Loopt er een abonnement, dan stopt het. Bestellingen bewaren we zeven jaar voor de
                belastingdienst, zonder koppeling aan je account. Dit kun je niet terugdraaien.
              </p>
              <label htmlFor={fieldId} className="mt-4 block text-[14px] text-ink" data-id="element-6562" data-mp="l3tmntqylzpz">
                Typ ter bevestiging je e-mailadres <span className="text-muted" data-id="element-6563" data-mp="g4qo8s5hirqz">({account.customer.email})</span>
              </label>
              <input id={fieldId} type="email" autoComplete="off" value={email} onChange={e => setEmail(e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} className={`${inputCls} mt-1.5 max-w-sm`} data-id="element-6564" data-mp="1wigvg7rwhw4u" />
              {reauth && error ? <div id={errorId} role="alert" className="mt-3 max-w-prose border border-clay/40 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-6565" data-mp="azo8no8fd1r4">
                  <p data-id="element-6566" data-mp="k2777w56gs7k">{error}</p>
                  <button type="button" onClick={signOut} className={`${btnPrimary} mt-3`} data-id="element-6567" data-mp="k14k7zaz79wx">
                    Opnieuw inloggen
                  </button>
                </div> : <FormError id={errorId} message={error} className="mt-3" data-id="element-6568" data-mp="tss8jrc8kau6" />}
              <div className="mt-4 flex flex-wrap gap-2" data-id="element-6569" data-mp="yr3is36g0ncx">
                <button type="submit" disabled={deleting} className={btnPrimary} data-id="element-6570" data-mp="ysmztvzxb2fr">
                  {deleting && <Spinner data-id="element-6571" data-mp="12qkmos681cfz" />}
                  {deleting ? 'Bezig met verwijderen…' : 'Verwijder mijn account'}
                </button>
                <button type="button" onClick={() => {
              setConfirming(false);
              setEmail('');
              setError(null);
            }} className={btnQuiet} data-id="element-6572" data-mp="2s9svtjiyx0tq">
                  Terug
                </button>
              </div>
            </form> : <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-6573" data-mp="3azsgzvbotvrv">
              <div className="min-w-0" data-id="element-6574" data-mp="3b1v5gax6zguh">
                <p className="text-[15px] text-ink" data-id="element-6575" data-mp="36y707yw18xce">Account verwijderen</p>
                <p className="mt-0.5 text-[13px] text-muted" data-id="element-6576" data-mp="327guulnlwuhp">Je krijgt eerst te zien wat er verdwijnt.</p>
              </div>
              <button type="button" onClick={() => setConfirming(true)} className={`${btnQuiet} text-muted`} data-id="element-6577" data-mp="2x7koifrfsqyz">
                Verwijderen
              </button>
            </div>}
        </div>
      </div>
    </section>;
}